import api from './api';

/**
 * Workspace Service
 * Handles all workspace-related API calls
 */
class WorkspaceService {

    /**
     * Get all workspaces for the current user
     *
     * @returns {Promise} Promise with user workspaces
     */
    getUserWorkspaces() {
        return api.get('/v1/workspaces');
    }

    /**
     * Get a specific workspace by ID
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise with workspace data
     */
    getWorkspace(workspaceId) {
        return api.get(`/v1/workspaces/${workspaceId}`);
    }

    /**
     * Create a new workspace
     *
     * @param {Object} workspaceData - Workspace creation data
     * @returns {Promise} Promise with created workspace
     */
    createWorkspace(workspaceData) {
        return api.post('/v1/workspaces', workspaceData);
    }

    /**
     * Update a workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @param {Object} workspaceData - Updated workspace data
     * @returns {Promise} Promise with updated workspace
     */
    updateWorkspace(workspaceId, workspaceData) {
        return api.put(`/v1/workspaces/${workspaceId}`, workspaceData);
    }

    /**
     * Delete a workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise resolving after deletion
     */
    deleteWorkspace(workspaceId) {
        return api.delete(`/v1/workspaces/${workspaceId}`);
    }

    /**
     * Get members of a workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise with workspace members
     */
    getWorkspaceMembers(workspaceId) {
        return api.get(`/v1/workspaces/${workspaceId}/members`);
    }

    /**
     * Add a member to workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @param {Object} memberData - Member data (email, role)
     * @returns {Promise} Promise with added member
     */
    addMember(workspaceId, memberData) {
        return api.post(`/v1/workspaces/${workspaceId}/members`, memberData);
    }

    /**
     * Update member role in workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @param {number} userId - User ID
     * @param {Object} roleData - Role update data
     * @returns {Promise} Promise with updated member
     */
    updateMemberRole(workspaceId, userId, roleData) {
        return api.put(`/v1/workspaces/${workspaceId}/members/${userId}`, roleData);
    }

    /**
     * Remove a member from workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @param {number} userId - User ID to remove
     * @returns {Promise} Promise resolving after removal
     */
    removeMember(workspaceId, userId) {
        return api.delete(`/v1/workspaces/${workspaceId}/members/${userId}`);
    }

    /**
     * Leave a workspace (remove yourself)
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise resolving after leaving
     */
    leaveWorkspace(workspaceId) {
        return api.post(`/v1/workspaces/${workspaceId}/leave`);
    }

    /**
     * Get workspace settings
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise with workspace settings
     */
    getWorkspaceSettings(workspaceId) {
        return api.get(`/v1/workspaces/${workspaceId}/settings`);
    }

    /**
     * Update workspace settings
     *
     * @param {string} workspaceId - Workspace ID
     * @param {Object} settings - Settings data
     * @returns {Promise} Promise with updated settings
     */
    updateWorkspaceSettings(workspaceId, settings) {
        return api.put(`/v1/workspaces/${workspaceId}/settings`, settings);
    }

    /**
     * Get workspace usage statistics
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise with usage statistics
     */
    getWorkspaceUsage(workspaceId) {
        return api.get(`/v1/workspaces/${workspaceId}/usage`);
    }

    /**
     * Generate workspace invite link
     *
     * @param {string} workspaceId - Workspace ID
     * @param {Object} inviteData - Invite configuration
     * @returns {Promise} Promise with invite link
     */
    generateInviteLink(workspaceId, inviteData) {
        return api.post(`/v1/workspaces/${workspaceId}/invite`, inviteData);
    }

    /**
     * Accept workspace invitation
     *
     * @param {string} inviteToken - Invitation token
     * @returns {Promise} Promise with workspace data
     */
    acceptInvitation(inviteToken) {
        return api.post(`/v1/workspaces/invite/accept`, { token: inviteToken });
    }

    /**
     * Decline workspace invitation
     *
     * @param {string} inviteToken - Invitation token
     * @returns {Promise} Promise resolving after declining
     */
    declineInvitation(inviteToken) {
        return api.post(`/v1/workspaces/invite/decline`, { token: inviteToken });
    }

    /**
     * Get workspace activity log
     *
     * @param {string} workspaceId - Workspace ID
     * @param {Object} params - Query parameters (page, limit, etc.)
     * @returns {Promise} Promise with activity log
     */
    getWorkspaceActivity(workspaceId, params = {}) {
        return api.get(`/v1/workspaces/${workspaceId}/activity`, { params });
    }

    /**
     * Get user's workspaces for My Workspaces page
     *
     * @param {number} currentWorkspaceId - Currently selected workspace ID
     * @returns {Promise} Promise with my workspaces data
     */
    getMyWorkspaces(currentWorkspaceId = null) {
        const params = currentWorkspaceId ? { currentWorkspaceId } : {};
        return api.get('/v1/workspaces/my-workspaces', { params });
    }

    /**
     * Set a workspace as user's default
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise with updated workspace
     */
    setDefaultWorkspace(workspaceId) {
        return api.post(`/v1/workspaces/${workspaceId}/set-default`);
    }

    /**
     * Rename a workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @param {string} newName - New workspace name
     * @returns {Promise} Promise with updated workspace
     */
    renameWorkspace(workspaceId, newName) {
        return api.put(`/v1/workspaces/${workspaceId}/rename`, { name: newName });
    }

    /**
     * Get workspace creation information including plan limits
     *
     * @returns {Promise} Promise with workspace creation info
     */
    getWorkspaceCreationInfo() {
        return api.get('/v1/workspaces/creation-info');
    }

    /**
     * Archive a workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise resolving after archiving
     */
    archiveWorkspace(workspaceId) {
        return api.post(`/v1/workspaces/${workspaceId}/archive`);
    }

    /**
     * Restore an archived workspace
     *
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise} Promise resolving after restoration
     */
    restoreWorkspace(workspaceId) {
        return api.post(`/v1/workspaces/${workspaceId}/restore`);
    }

    /**
     * Transfer workspace ownership
     *
     * @param {string} workspaceId - Workspace ID
     * @param {number} newOwnerId - New owner user ID
     * @returns {Promise} Promise resolving after transfer
     */
    transferOwnership(workspaceId, newOwnerId) {
        return api.post(`/v1/workspaces/${workspaceId}/transfer`, {
            newOwnerId
        });
    }
}

export default new WorkspaceService();